/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

// Mirrors packages/backend/src/extensions/networkPolicy.ts (the API shapes).

export type PortRule = { port: number; protocol?: 'TCP' | 'UDP' };

export type CalledByEntry = {
  name: string;
  namespace: string;
  ports?: PortRule[];
};

export type ExposureSummary = { external: boolean; internal: boolean };

export type ServiceNode = {
  name: string;
  entityRef: string;
  team: string | null;
  namespace: string | null;
  repo: { owner: string; name: string } | null;
  envs: string[];
  policyEnabled: Record<string, boolean>;
  autoLockdownActive: Record<string, boolean>;
  chartRev: Record<string, string | null>;
  exposure: Record<string, ExposureSummary>;
  fetchError?: string;
};

export type IngressEdge = {
  id: string;
  caller: string | null;
  callerName: string;
  callerNamespace: string;
  callee: string;
  ports: PortRule[] | null;
  portsDiffer: boolean;
  envs: string[];
};

export type NetworkPolicyGraph = {
  nodes: ServiceNode[];
  edges: IngressEdge[];
  generatedAt: string;
};

export type Change =
  | { op: 'addCaller'; service: string; caller: CalledByEntry; envs: string[] | 'all' }
  | {
      op: 'removeCaller';
      service: string;
      callerName: string;
      callerNamespace: string;
      envs: string[] | 'all';
    }
  | {
      op: 'setPorts';
      service: string;
      callerName: string;
      callerNamespace: string;
      ports: PortRule[] | null;
      envs: string[] | 'all';
    }
  | { op: 'enablePolicy'; service: string; callers: CalledByEntry[]; envs: string[] | 'all' }
  | { op: 'promoteEnv'; service: string; fromEnv: string; toEnv: string };

export type ServiceDetail = {
  node: ServiceNode;
  // Base gitops/argocd/values.yaml calledBy, if the repo has one. Helm
  // deep-merges maps but replaces lists wholesale, so this only reaches an
  // env whose own file doesn't define a non-empty calledBy of its own.
  base: { path: string; calledBy: CalledByEntry[] } | null;
  files: {
    env: string;
    path: string;
    // Effective calledBy for this env (own list, or base's as a fallback).
    hasPolicy: boolean;
    calledBy: CalledByEntry[];
    // This env file's own calledBy, before falling back to base.
    envCalledBy: CalledByEntry[];
    // True when envCalledBy is empty and base's list is what's actually
    // effective here -- i.e. `calledBy` above came from `base`, not this file.
    usesBaseFallback: boolean;
    chartRev: string | null;
    exposure: ExposureSummary;
    autoLockdownActive: boolean;
    autoLockdownOptOut: boolean;
    parseError?: string;
  }[];
  callees: IngressEdge[];
};

export type DryRunRepo = {
  service: string;
  repo: string;
  summaries: string[];
  enablePolicy: boolean;
  // Non-fatal caveats about the change (e.g. a base-level caller silently
  // shadowed by a newly-non-empty env list). Always show these before submit.
  warnings: string[];
  files: { path: string; before: string; after: string }[];
};

export type SubmitResult = {
  service: string;
  repo: string;
  // 'exists' = an already-open PR for this exact change was found and
  // reused instead of creating a duplicate; 'updated' = an already-open PR
  // had this change's edits pushed onto it; 'failed' = this repo's edit
  // didn't apply (other repos in the same submit may still have succeeded).
  status: 'created' | 'exists' | 'updated' | 'failed';
  prUrl: string | null;
  detail: string;
  error?: string;
};

// Policy posture of a node, derived from policyEnabled across its envs.
export const policyState = (node: ServiceNode): 'locked' | 'partial' | 'open' => {
  const values = node.envs.map(e => node.policyEnabled[e] ?? false);
  if (values.length > 0 && values.every(Boolean)) return 'locked';
  if (values.some(Boolean)) return 'partial';
  return 'open';
};

// True when chart autoLockdown would admit Traefik+baseline in any env.
export const hasTraefikLockdown = (node: ServiceNode): boolean =>
  node.envs.some(e => node.autoLockdownActive[e]);

// Truly allow-all: no calledBy peer list AND no Traefik autoLockdown.
export const isTrulyOpen = (node: ServiceNode): boolean =>
  policyState(node) === 'open' && !hasTraefikLockdown(node);

const parseChartPin = (
  rev: string,
): { chart: string; parts: [number, number, number] } | null => {
  const m = rev.match(/^([a-z0-9-]+)-(\d+)\.(\d+)\.(\d+)$/i);
  if (!m) return null;
  return {
    chart: m[1].toLowerCase(),
    parts: [Number(m[2]), Number(m[3]), Number(m[4])],
  };
};

// Mirrors the backend's compareChartRevisions (networkPolicyValues.ts):
// negative a < b, 0 equal, positive a > b, null not comparable (missing --
// even when BOTH are missing -- unparseable, or different chart families).
// The promote UI must only offer hops the backend would accept: it refuses
// null and negative, and it requires a terraform pin on both envs.
export const compareChartRevisions = (
  a: string | null,
  b: string | null,
): number | null => {
  if (!a || !b) return null;
  if (a === b) return 0;
  const pa = parseChartPin(a);
  const pb = parseChartPin(b);
  if (!pa || !pb || pa.chart !== pb.chart) return null;
  for (let i = 0; i < 3; i += 1) {
    if (pa.parts[i] !== pb.parts[i]) return pa.parts[i] - pb.parts[i];
  }
  return 0;
};

// Promotion ladder, lowest env first. A hop runs from each env a service has
// to the next one it has, so a repo not yet on staging keeps its gamedev ->
// prod promote instead of losing prod altogether; `skipped` names the gap.
export const PROMOTE_LADDER = [
  'platform-ci',
  'platform-gamedev',
  'platform-staging',
  'platform-prod',
];

export type PromoteHop = {
  from: string;
  to: string;
  label: string;
  skipped: string[];
};

export const promoteHops = (envs: string[]): PromoteHop[] => {
  const present = PROMOTE_LADDER.filter(e => envs.includes(e));
  return present.slice(1).map((to, i) => {
    const from = present[i];
    return {
      from,
      to,
      label: `Promote to ${to.replace('platform-', '')}`,
      skipped: PROMOTE_LADDER.slice(
        PROMOTE_LADDER.indexOf(from) + 1,
        PROMOTE_LADDER.indexOf(to),
      ),
    };
  });
};
